import cx from 'classnames'
import { motion, Variants } from 'motion/react'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useResizeObserver } from 'usehooks-ts'

import { SanityMenuLinkFragment } from '@data/sanity/queries/types/site'
import { SiteContext } from '@lib/site-context'

import Menu from './menu'

interface MegaNavigationDropdownProps {
  dropdown: SanityMenuLinkFragment
  hasFocus: boolean
  setHasFocus: (hasFocus: boolean) => void
  setActiveDropdownHeight: (activeDropdownHeight: number) => void
}

const swipeAnimation: Variants = {
  show: {
    opacity: 1,
    x: ['-1rem', '0rem'],
    transition: {
      x: {
        duration: 0.8,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1],
      },
      opacity: {
        duration: 0.2,
        delay: 0.1,
      },
    },
  },
  hide: {
    x: ['0rem', '1rem'],
    opacity: 0,
    transition: {
      x: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1],
      },
      opacity: {
        duration: 0.1,
      },
    },
  },
}

const MegaNavigationDropdown = ({
  dropdown,
  hasFocus,
  setHasFocus,
  setActiveDropdownHeight,
}: MegaNavigationDropdownProps) => {
  const { megaNavigation, toggleMegaNavigation } = useContext(SiteContext)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownRectangle = useResizeObserver({
    ref: dropdownRef,
  })

  const isDropdownActive = useMemo(() => {
    return megaNavigation.isOpen && megaNavigation.activeId === dropdown._key
  }, [dropdown, megaNavigation.activeId, megaNavigation.isOpen])

  useEffect(() => {
    if (isDropdownActive && typeof dropdownRectangle.height !== 'undefined') {
      setActiveDropdownHeight(dropdownRectangle.height)
    }
  }, [dropdownRectangle.height, isDropdownActive, setActiveDropdownHeight])

  return (
    <div
      ref={dropdownRef}
      id={`meganav-${dropdown._key}`}
      className={cx(
        'absolute top-0 inset-x-0 z-10 -mt-px overflow-hidden',
        'transition-visibility duration-200',
        {
          'delay-[0s] pointer-events-auto': isDropdownActive,
          'invisible delay-[.8s] pointer-events-none': !isDropdownActive,
        },
      )}
    >
      <div>
        <motion.div
          initial="hide"
          animate={isDropdownActive ? 'show' : 'hide'}
          onAnimationComplete={(definition) =>
            setHasFocus(definition === 'show')
          }
          variants={swipeAnimation}
          className="container grid grid-cols-2 relative pt-10 pb-12 will-change-transform"
        >
          <Menu
            items={dropdown.dropdownItems}
            hasFocus={hasFocus && isDropdownActive}
            onClick={() => toggleMegaNavigation(false)}
            isMegaNavMenu
          />
        </motion.div>
      </div>
    </div>
  )
}

export default MegaNavigationDropdown
